import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import type { FieldOptions, Operator } from '../../../types/QueryBuilder';

import { fieldAllowsMultipleValues, isRegexOperator } from './multiValue';
import { OPERATOR_DISPLAY_MAP } from './operators';
import { isPresentAbsence } from './presentSentinel';
import { quoteIfNeeded } from './tokenString';

/**
 * How long to wait before promoting a match whose operator could still grow with the
 * next keystroke (`duration>` → `duration>=`, `span_name is` → `span_name is not`).
 *
 * This is a "the user stopped typing" signal, not the main path — a keystroke that rules
 * the longer operator out promotes immediately. Erring long is cheap (the lozenge forms a
 * beat later) while erring short is not (a slow typist reaching for `not` would get
 * `span_name is` with `not` as its value), so this sits well past a normal typing cadence.
 */
export const INLINE_FILTER_PROMOTION_DELAY_MS = 600;

/** One way an operator can be spelled while typing: the symbol, or its display label. */
type OperatorSpelling = {
  operator: Operator;
  /** Lowercased text the user types for this operator. */
  text: string;
  /** Word spellings must be separated from the field key by whitespace and end on a word boundary. */
  word: boolean;
  matcher: RegExp;
  /** Unary exists spellings serialize the portable present sentinel instead of an editable value. */
  present?: boolean;
};

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildSpelling(operator: Operator, text: string, word: boolean, present = false): OperatorSpelling {
  const matcher = word
    ? new RegExp(`^\\s+${text.split(' ').map(escapeRegExp).join('\\s+')}(?=\\s|$)`, 'i')
    : new RegExp(`^\\s*${escapeRegExp(text)}`);
  return { operator, text: text.toLowerCase(), word, matcher, present };
}

const OPERATORS = Object.keys(OPERATOR_DISPLAY_MAP) as Operator[];

/**
 * `=^` (ends with) is deliberately not typeable: it starts with `=`, so honouring it would
 * force every `field=` to wait for a possible `^` before becoming a lozenge — delaying the
 * most common trigger. Its "ends with" spelling and the operator picker still reach it.
 */
const UNTYPEABLE_SYMBOLS = new Set<Operator>(['=^']);

/** Longest spelling first, so `is not` wins over `is` and `>=` over `>`. */
const SPELLINGS: OperatorSpelling[] = [
  ...OPERATORS.filter((operator) => !UNTYPEABLE_SYMBOLS.has(operator)).map((operator) =>
    buildSpelling(operator, operator, false),
  ),
  ...OPERATORS.map((operator) => buildSpelling(operator, OPERATOR_DISPLAY_MAP[operator], true)),
  buildSpelling('=', 'exists', true, true),
  buildSpelling('!=', 'does not exist', true, true),
  buildSpelling('!=', 'not exists', true, true),
].sort((a, b) => b.text.length - a.text.length);

export type InlineFilterMatch = {
  /** Canonical field key from the supplied options (the user may have typed another casing). */
  field: string;
  operator: Operator;
  /** Text typed after the operator; empty when the user stopped at the operator. */
  value: string;
  negated: boolean;
  /**
   * A longer operator still starts with everything typed so far, so the caller should wait
   * `INLINE_FILTER_PROMOTION_DELAY_MS` before acting on this match in case the user keeps typing.
   */
  pending: boolean;
};

/** Longest field key the text starts with, compared case-insensitively. */
function matchFieldPrefix(text: string, fieldOptions: readonly FieldOptions[]): FieldOptions | undefined {
  const lower = text.toLowerCase();
  let match: FieldOptions | undefined;
  for (const option of fieldOptions) {
    if (lower.startsWith(option.field.toLowerCase()) && option.field.length > (match?.field.length ?? 0)) {
      match = option;
    }
  }
  return match;
}

export type ExactFieldTrailingSpaceMatch = {
  field: string;
  negated: boolean;
};

export function matchExactFieldTrailingSpace(
  text: string,
  fieldOptions: readonly FieldOptions[] | undefined,
): ExactFieldTrailingSpaceMatch | null {
  if (!fieldOptions?.length || !/\s$/.test(text)) {
    return null;
  }

  const trimmedStart = text.trimStart();
  const negated = trimmedStart.startsWith('!');
  const body = (negated ? trimmedStart.slice(1) : trimmedStart).trimEnd();
  if (!body || /\s/.test(body)) {
    return null;
  }

  const fieldOption = fieldOptions.find((option) => option.field.toLowerCase() === body.toLowerCase());
  return fieldOption ? { field: fieldOption.field, negated } : null;
}

/**
 * Recognise a filter the user is typing directly into a free-text cell — `span_name=`,
 * `duration >= 100`, `span_name is not checkout`, `!service_name contains api` — so the bar can
 * turn it into a lozenge instead of leaving it as free text.
 *
 * Returns null unless the text starts with a known field key followed by an operator: unknown
 * keys stay free text (the "Search for field:" option still promotes them), and a field whose
 * `allowedOperators` excludes the typed operator is left alone.
 */
export function matchInlineFilter(
  text: string,
  fieldOptions: readonly FieldOptions[] | undefined,
  allowExists = true,
): InlineFilterMatch | null {
  if (!fieldOptions?.length) {
    return null;
  }

  const trimmed = text.trimStart();
  const negated = trimmed.startsWith('!');
  const afterNegation = negated ? trimmed.slice(1) : trimmed;

  const fieldOption = matchFieldPrefix(afterNegation, fieldOptions);
  if (!fieldOption) {
    return null;
  }

  const tail = afterNegation.slice(fieldOption.field.length);
  const spelling = SPELLINGS.find((candidate) => candidate.matcher.test(tail));
  if (!spelling) {
    return null;
  }
  if (
    spelling.present
      ? !allowExists
      : fieldOption.allowedOperators && !fieldOption.allowedOperators.includes(spelling.operator)
  ) {
    return null;
  }

  const operatorText = spelling.matcher.exec(tail)?.[0] ?? '';
  // Collapse the separators so `span_name  is  not` compares against the canonical spellings.
  const typedOperator = tail.replace(/\s+/g, ' ').trimStart().toLowerCase();
  const operatorCommittedBySpace = /\s$/.test(typedOperator);

  // Presence is the XOR of the `!` prefix and the `!=` operator, but the operator cell reads the
  // operator alone — keeping both would label `!field exists` as "exists" while the query means
  // absence. Fold a typed `!` into the operator and drop the prefix, which is also the form
  // `antlrToLezer` emits so the cell's exists toggle round-trips.
  const absent = spelling.present && isPresentAbsence({ negated, operator: spelling.operator });

  return {
    field: fieldOption.field,
    operator: spelling.present ? (absent ? '!=' : '=') : spelling.operator,
    value: spelling.present ? PRESENT_VALUE_SENTINEL : tail.slice(operatorText.length).trim(),
    negated: spelling.present ? false : negated,
    pending:
      !operatorCommittedBySpace &&
      SPELLINGS.some(
        (candidate) => candidate.text.length > spelling.text.length && candidate.text.startsWith(typedOperator),
      ),
  };
}

function normalizeTypedQuotes(text: string): string {
  return text.replace(/[\u201C\u201D]/g, '"');
}

export function unwrapTypedValue(raw: string): string {
  const normalized = normalizeTypedQuotes(raw);
  const quoted = /^(\s*)"((?:\\.|[^"\\])*)"\s*\)?\s*$/.exec(normalized);
  if (quoted) {
    return quoted[2].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  return normalized.replaceAll('"', '');
}

/** Regex values are stored with their `/` delimiters — see `parsedQueryToAntlr` and the value cell. */
function asRegexLiteral(pattern: string): string {
  return pattern.length >= 2 && pattern.startsWith('/') && pattern.endsWith('/') ? pattern : `/${pattern}/`;
}

/**
 * Serialize a match's typed value for the grammar's value slot.
 *
 * A comma only separates values where the filter can hold more than one, so `a, b` becomes two
 * trimmed values on a plain string field while a regex body, a comparison operator's operand, and
 * a boolean keep the comma as typed.
 */
export function inlineFilterValueText(match: InlineFilterMatch, fieldOption: FieldOptions | undefined): string {
  const typedValue = isRegexOperator(match.operator) ? match.value : unwrapTypedValue(match.value);
  if (typedValue === '') {
    return '""';
  }
  if (isRegexOperator(match.operator)) {
    return quoteIfNeeded(asRegexLiteral(typedValue));
  }
  if (!fieldAllowsMultipleValues(fieldOption, match.operator)) {
    return quoteIfNeeded(typedValue);
  }
  const values = typedValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  return values.length > 0 ? values.map(quoteIfNeeded).join(',') : '""';
}
